/**
 * Modern Google Maps Place Autocomplete using PlaceAutocompleteElement
 * Replaces deprecated google.maps.places.Autocomplete
 */

/**
 * Initialize Google Places Autocomplete (Classic API - More Reliable)
 * @param {string} containerId - ID of the div container where autocomplete input will be placed
 * @param {Function} onPlaceSelect - Callback when a place is selected
 * @param {Object} options - Configuration options
 * @param {string} initialValue - Initial value to pre-fill in the input
 * @returns {Object} The autocomplete instance and input element
 */
export const initPlaceAutocomplete = (containerId, onPlaceSelect, options = {}, initialValue = '') => {
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps Places library not loaded');
    return null;
  }

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with ID "${containerId}" not found`);
    return null;
  }

  // Clear existing content
  container.innerHTML = '';

  // Create a regular input element
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = options.placeholder || 'Start typing your address...';
  input.value = initialValue || '';
  input.className = options.className || 'w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all text-sm';
  input.style.fontSize = '16px'; // Prevent iOS zoom
  input.setAttribute('autocomplete', 'off'); // Disable browser autocomplete
  input.setAttribute('autocorrect', 'off');
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('spellcheck', 'false');
  // Mark as Google autocomplete input so form handlers can skip it
  input.setAttribute('data-google-autocomplete', 'true');

  container.appendChild(input);

  // Initialize classic Autocomplete
  const autocomplete = new window.google.maps.places.Autocomplete(input, {
    componentRestrictions: { country: 'au' },  // Australia only
    fields: ['formatted_address', 'geometry', 'name'],
    ...options
  });

  console.log('✅ Google Places Autocomplete (Classic API) initialized with value:', initialValue);

  // Listen for place selection
  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    console.log('🎯 Place selected!', place);

    if (!place || !place.geometry) {
      console.log('⚠️ No geometry in place');
      return;
    }

    // Call the callback with place data
    if (onPlaceSelect) {
      onPlaceSelect({
        formatted_address: place.formatted_address,
        display_name: place.name,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        place: place
      });
    }
  });

  // Ensure pac-container z-index is high enough (one-time fix when dropdown first appears)
  const ensureZIndex = () => {
    const pacContainers = document.querySelectorAll('.pac-container');
    pacContainers.forEach(pc => {
      if (pc.style.zIndex !== '999999') {
        pc.style.zIndex = '999999';
      }
    });
  };

  input.addEventListener('input', ensureZIndex);
  input.addEventListener('focus', ensureZIndex);

  return { autocomplete, input };
};

/**
 * Destroy PlaceAutocompleteElement instance
 * @param {string} containerId - ID of the container
 */
export const destroyPlaceAutocomplete = (containerId) => {
  const container = document.getElementById(containerId);
  if (container) {
    // Remove all event listeners by cloning and replacing the element
    const input = container.querySelector('input');
    if (input) {
      const newInput = input.cloneNode(true);
      input.parentNode.replaceChild(newInput, input);
    }
    // Clear the container
    container.innerHTML = '';
    console.log('🧹 Autocomplete destroyed for:', containerId);
  }
};
